import React, { type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches render errors so the app shows a message instead of a blank screen.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env?.DEV;
      return (
        <div
          style={{
            padding: '2rem',
            maxWidth: 640,
            margin: '0 auto',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>
            Something went wrong
          </h1>
          <p style={{ marginBottom: '1rem' }}>
            A runtime error stopped the page from rendering. Open the browser
            developer console (F12) for the full stack trace.
          </p>
          {isDev && this.state.error ? (
            <pre
              style={{
                background: 'var(--brand-surface-light)',
                padding: '1rem',
                overflow: 'auto',
                fontSize: 12,
                borderRadius: 4,
              }}
            >
              {String(this.state.error)}
            </pre>
          ) : null}
          <p style={{ marginTop: '1rem' }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '0.5rem 1rem',
                cursor: 'pointer',
              }}
            >
              Reload page
            </button>
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
